import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, ExternalLink, Loader2, RefreshCw, Sparkles, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import type { TestResult } from '@/lib/types';

interface StartResponse {
  wizardId: string;
  userCode: string;
  verificationUri: string;
  expiresIn: number;
  interval: number;
}

interface PollResponse {
  state: 'pending' | 'done' | 'expired' | 'denied' | 'error';
  message?: string;
  appCreated?: boolean;
}

type Phase =
  | { step: 'idle' }
  | { step: 'login'; start: StartResponse }
  | { step: 'verify'; message: string }
  | { step: 'success'; message: string }
  | { step: 'failed'; message: string };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Einrichtungsassistent: Administrator meldet sich per Gerätecode bei Microsoft an,
 * das Portal legt die App-Registrierung samt Berechtigungen und Schlüssel selbst an.
 */
export function M365Wizard({ connected, onDone }: { connected: boolean; onDone?: () => void }) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>({ step: 'idle' });
  const cancelled = useRef(false);

  useEffect(
    () => () => {
      cancelled.current = true;
    },
    [],
  );

  const copy = (code: string) =>
    navigator.clipboard.writeText(code).then(
      () => toast.success('Code kopiert'),
      () => undefined,
    );

  /** Neue Zugangsdaten brauchen bei Microsoft bis zu einigen Minuten, bis sie gelten. */
  async function verify(message: string) {
    setPhase({ step: 'verify', message });
    let last: TestResult | null = null;
    for (let i = 0; i < 12 && !cancelled.current; i++) {
      await sleep(i === 0 ? 3000 : 10_000);
      last = await api<TestResult>('/integrations/m365/connection/test', { method: 'POST' }).catch(() => null);
      if (last?.ok) break;
    }
    qc.invalidateQueries({ queryKey: ['integrations', 'm365'] });
    if (cancelled.current) return;
    if (last?.ok) {
      setPhase({ step: 'success', message });
      onDone?.();
    } else {
      setPhase({
        step: 'failed',
        message: `Die App wurde angelegt, die Verbindung ist aber noch nicht bereit${last ? ` (${last.message})` : ''}. Microsoft braucht manchmal einige Minuten – bitte später „Verbindung testen“ klicken.`,
      });
    }
  }

  async function poll(start: StartResponse) {
    const deadline = Date.now() + start.expiresIn * 1000;
    while (!cancelled.current && Date.now() < deadline) {
      await sleep(Math.max(start.interval, 2) * 1000);
      let r: PollResponse;
      try {
        r = await api<PollResponse>(`/integrations/m365/wizard/${start.wizardId}/poll`, { method: 'POST' });
      } catch (e) {
        setPhase({ step: 'failed', message: errorMessage(e) });
        return;
      }
      if (r.state === 'pending') continue;
      if (r.state === 'done') {
        qc.invalidateQueries({ queryKey: ['integrations', 'm365'] });
        await verify(r.message ?? 'Eingerichtet.');
      } else {
        setPhase({ step: 'failed', message: r.message ?? 'Die Einrichtung ist fehlgeschlagen.' });
      }
      return;
    }
    if (!cancelled.current) setPhase({ step: 'failed', message: 'Der Code ist abgelaufen. Bitte neu starten.' });
  }

  const start = useMutation({
    mutationFn: () => api<StartResponse>('/integrations/m365/wizard/start', { method: 'POST' }),
    onSuccess: (s) => {
      cancelled.current = false;
      setPhase({ step: 'login', start: s });
      void copy(s.userCode);
      void poll(s);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const abort = () => {
    cancelled.current = true;
    if (phase.step === 'login') void api(`/integrations/m365/wizard/${phase.start.wizardId}`, { method: 'DELETE' }).catch(() => undefined);
    setPhase({ step: 'idle' });
  };

  return (
    <Card className={connected ? undefined : 'border-primary/40'}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="size-4 text-primary" />
          {connected ? 'Mit Assistent neu verbinden' : 'Mit Microsoft 365 verbinden'}
        </CardTitle>
        <CardDescription>
          {connected
            ? 'Erzeugt einen neuen Schlüssel für die vorhandene App (z. B. vor Ablauf) oder verbindet ein anderes Microsoft-365-Konto.'
            : 'Ein Administrator meldet sich einmal bei Microsoft an – das Portal legt die App-Registrierung, die Berechtigungen und den Schlüssel selbst an. In Entra ID muss nichts von Hand eingerichtet werden.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {phase.step === 'idle' && (
          <div>
            <Button onClick={() => start.mutate()} loading={start.isPending}>
              {connected ? <RefreshCw /> : <Sparkles />} {connected ? 'Assistent starten' : 'Jetzt verbinden'}
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              Benötigt ein Konto mit der Rolle „Globaler Administrator“ (oder „Administrator für privilegierte Rollen“). Die Administratoranmeldung wird nur für die Einrichtung
              verwendet und nicht gespeichert.
            </p>
          </div>
        )}

        {phase.step === 'login' && (
          <div className="grid gap-4">
            <ol className="grid list-decimal gap-1 pl-5 text-sm">
              <li>Microsoft-Anmeldeseite öffnen.</li>
              <li>Den Code eingeben (er ist bereits in der Zwischenablage).</li>
              <li>Mit dem Administratorkonto anmelden und die Berechtigungen bestätigen.</li>
            </ol>
            <div className="flex flex-wrap items-center gap-3">
              <code className="rounded-lg border bg-muted/50 px-4 py-2 font-mono text-2xl font-semibold tracking-widest" aria-label="Anmeldecode">
                {phase.start.userCode}
              </code>
              <Button variant="outline" size="icon" aria-label="Code kopieren" onClick={() => copy(phase.start.userCode)}>
                <Copy />
              </Button>
              <Button asChild>
                <a href={phase.start.verificationUri} target="_blank" rel="noreferrer">
                  Microsoft-Anmeldung öffnen <ExternalLink />
                </a>
              </Button>
            </div>
            <p className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
              <Loader2 className="size-4 animate-spin" /> Warte auf die Anmeldung bei Microsoft …
            </p>
            <div>
              <Button variant="ghost" size="sm" onClick={abort}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}

        {phase.step === 'verify' && (
          <div className="grid gap-2 text-sm" aria-live="polite">
            <p className="flex items-center gap-2 font-medium text-success">
              <CheckCircle2 className="size-4" /> {phase.message}
            </p>
            <p className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Verbindung wird geprüft – neue Zugangsdaten sind bei Microsoft erst nach kurzer Zeit aktiv (bis zu 2 Minuten) …
            </p>
          </div>
        )}

        {phase.step === 'success' && (
          <Alert variant="success">
            <p className="font-medium">Microsoft 365 ist verbunden.</p>
            <p className="mt-1">Als Nächstes unten unter „Postfächer“ das Service-Postfach hinzufügen (z. B. service@…) und als Standard-Absender markieren.</p>
          </Alert>
        )}

        {phase.step === 'failed' && (
          <div className="grid gap-3">
            <Alert variant="destructive">
              <p className="flex items-start gap-2">
                <XCircle className="mt-0.5 size-4 shrink-0" /> {phase.message}
              </p>
            </Alert>
            <div>
              <Button variant="outline" onClick={() => setPhase({ step: 'idle' })}>
                Erneut versuchen
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
